import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ReferenceLine, BarChart, Bar
} from 'recharts';
import {
  TrendingUp, TrendingDown, Search, Plus, Trash2, RefreshCw,
  Zap, Target, AlertTriangle, Shield, Clock, BarChart2,
  ChevronUp, ChevronDown, ArrowUpRight, ArrowDownRight,
  Star, StarOff, Activity, Brain, BookOpen, Layers, X,
  DollarSign, Percent, Eye, CheckCircle2, XCircle, Info
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DEFAULT_WATCHLIST = ['BTC', 'ETH', 'SOL', 'BNB', 'AVAX'];
const TIMEFRAMES = [
  { label: '7D',  days: 7 },
  { label: '30D', days: 30 },
  { label: '90D', days: 90 },
  { label: '1A',  days: 365 },
];

// ── Formatters ────────────────────────────────────────────────────────────────
const fmtPrice = (v) => {
  if (!v && v !== 0) return '—';
  if (v >= 1000) return '$' + v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (v >= 1) return '$' + v.toFixed(4);
  return '$' + v.toFixed(8);
};
const fmtPct = (v) => (v >= 0 ? '+' : '') + (v || 0).toFixed(2) + '%';
const fmtNum = (v) => {
  if (!v) return '—';
  if (v >= 1e9) return '$' + (v / 1e9).toFixed(2) + 'B';
  if (v >= 1e6) return '$' + (v / 1e6).toFixed(2) + 'M';
  if (v >= 1e3) return '$' + (v / 1e3).toFixed(1) + 'K';
  return '$' + v.toFixed(2);
};

// ── RSI Gauge ─────────────────────────────────────────────────────────────────
function RSIGauge({ value = 50 }) {
  const clamped = Math.max(0, Math.min(100, value));
  const angle = -90 + (clamped / 100) * 180;
  const color = clamped < 30 ? '#22C55E' : clamped > 70 ? '#EF4444' : '#F59E0B';
  const label = clamped < 30 ? 'SOBREVENTA' : clamped > 70 ? 'SOBRECOMPRA' : 'NEUTRAL';
  const r = 38, cx = 50, cy = 48;
  const startAngle = -180 * (Math.PI / 180);
  const endAngle = 0 * (Math.PI / 180);
  const needleAngle = (-90 + (clamped / 100) * 180) * (Math.PI / 180);
  const nx = cx + r * Math.cos(needleAngle);
  const ny = cy + r * Math.sin(needleAngle);
  return (
    <div className="flex flex-col items-center">
      <svg viewBox="0 0 100 55" width="120" height="70">
        {/* Track */}
        <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`} fill="none" stroke="#1a1a1a" strokeWidth="8" />
        {/* Colored zones */}
        <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx} ${cy - r}`} fill="none" stroke="#22C55E" strokeWidth="8" opacity="0.4" />
        <path d={`M ${cx} ${cy - r} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`} fill="none" stroke="#EF4444" strokeWidth="8" opacity="0.4" />
        {/* Needle */}
        <line x1={cx} y1={cy} x2={nx} y2={ny} stroke={color} strokeWidth="2.5" strokeLinecap="round" />
        <circle cx={cx} cy={cy} r="3" fill={color} />
        <text x={cx} y={cy + 10} textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">{Math.round(clamped)}</text>
      </svg>
      <span style={{ color, fontSize: '10px', fontWeight: 700, letterSpacing: '0.05em' }}>{label}</span>
    </div>
  );
}

// ── Mini Sparkline ────────────────────────────────────────────────────────────
function Sparkline({ data = [], positive = true, width = 80, height = 32 }) {
  if (!data || data.length < 2) return <div style={{ width, height }} />;
  const min = Math.min(...data), max = Math.max(...data);
  const range = max - min || 1;
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * width},${height - ((v - min) / range) * (height - 4) - 2}`).join(' ');
  const color = positive ? '#22C55E' : '#EF4444';
  return (
    <svg width={width} height={height}>
      <defs>
        <linearGradient id={`sl-${positive}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polyline points={`${pts} ${width},${height} 0,${height}`} fill={`url(#sl-${positive})`} />
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" />
    </svg>
  );
}

// ── Signal Badge ──────────────────────────────────────────────────────────────
function SignalBadge({ signal }) {
  const cfg = {
    BUY:  { color: '#22C55E', bg: 'rgba(34,197,94,0.15)', border: 'rgba(34,197,94,0.3)', label: '● COMPRAR', icon: TrendingUp },
    SELL: { color: '#EF4444', bg: 'rgba(239,68,68,0.15)',  border: 'rgba(239,68,68,0.3)',  label: '● VENDER',  icon: TrendingDown },
    HOLD: { color: '#F59E0B', bg: 'rgba(245,158,11,0.15)', border: 'rgba(245,158,11,0.3)', label: '● ESPERAR', icon: Target },
  }[signal] || { color: '#6B7280', bg: 'rgba(107,114,128,0.15)', border: 'rgba(107,114,128,0.3)', label: '● —', icon: Target };
  const Icon = cfg.icon;
  return (
    <span style={{ color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.border}` }}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-bold tracking-wide">
      <Icon className="w-4 h-4" /> {cfg.label}
    </span>
  );
}

// ── Custom Chart Tooltip ──────────────────────────────────────────────────────
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  const date = d?.ts ? new Date(d.ts).toLocaleDateString('es-MX', { month: 'short', day: 'numeric' }) : '';
  return (
    <div className="glass-panel p-3 text-xs space-y-1 border border-white/10 rounded-xl shadow-xl">
      <p className="text-gray-400 font-medium">{date}</p>
      <p className="text-white font-mono font-bold">{fmtPrice(d?.close)}</p>
      {d?.open && <p className="text-gray-400">O: {fmtPrice(d.open)} · H: {fmtPrice(d.high)} · L: {fmtPrice(d.low)}</p>}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function OkTradeModule({ user }) {
  const [selectedSymbol, setSelectedSymbol] = useState('BTC');
  const [analysis, setAnalysis] = useState(null);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [timeframe, setTimeframe] = useState(30);
  const [watchlist, setWatchlist] = useState([]);
  const [watchlistPrices, setWatchlistPrices] = useState({});
  const [loadingWL, setLoadingWL] = useState(false);
  const [movers, setMovers] = useState({ gainers: [], losers: [] });
  const [topCoins, setTopCoins] = useState([]);
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [paperTrades, setPaperTrades] = useState([]);
  const [showPaperForm, setShowPaperForm] = useState(false);
  const [paperForm, setPaperForm] = useState({ action: 'BUY', quantity: '', entry_price: '', stop_loss: '', take_profit: '', notes: '' });
  const [activeSection, setActiveSection] = useState('chart'); // chart | indicators | trades | market
  const searchRef = useRef(null);

  useEffect(() => { loadAll(); }, []);
  useEffect(() => { loadAnalysis(); }, [selectedSymbol, timeframe]);

  const loadAll = async () => {
    await Promise.all([loadWatchlist(), loadMovers(), loadTopCoins()]);
    if (user?.id) loadPaperTrades();
  };

  const loadAnalysis = async () => {
    setLoadingAnalysis(true);
    setAnalysis(null);
    try {
      const r = await axios.get(`${API}/trading/analysis/${selectedSymbol}`, { params: { days: timeframe } });
      setAnalysis(r.data);
      // Pre-fill paper trade price
      if (r.data?.quote?.price) {
        setPaperForm(f => ({ ...f, entry_price: r.data.quote.price.toFixed(4) }));
      }
    } catch (e) {
      toast.error(`Error al cargar análisis de ${selectedSymbol}`);
    } finally {
      setLoadingAnalysis(false);
    }
  };

  const loadWatchlist = async () => {
    if (!user?.id) return;
    setLoadingWL(true);
    try {
      const r = await axios.get(`${API}/trading/watchlist/${user.id}`);
      const wl = r.data.watchlist || [];
      setWatchlist(wl.length > 0 ? wl : DEFAULT_WATCHLIST.map(s => ({ symbol: s, type: 'crypto' })));
      // Load live prices for all watchlist items
      const syms = (wl.length > 0 ? wl : DEFAULT_WATCHLIST.map(s => ({ symbol: s }))).map(i => i.symbol).join(',');
      if (syms) await loadWatchlistPrices(syms.split(','));
    } catch (e) {
      setWatchlist(DEFAULT_WATCHLIST.map(s => ({ symbol: s, type: 'crypto' })));
    } finally {
      setLoadingWL(false);
    }
  };

  const loadWatchlistPrices = async (symbols) => {
    try {
      const ids = symbols.map(s => {
        const COIN_IDS = { BTC:'bitcoin',ETH:'ethereum',BNB:'binancecoin',SOL:'solana',XRP:'ripple',ADA:'cardano',DOGE:'dogecoin',DOT:'polkadot',MATIC:'matic-network',AVAX:'avalanche-2',LINK:'chainlink' };
        return COIN_IDS[s.toUpperCase()] || s.toLowerCase();
      }).join(',');
      const r = await axios.get(`https://api.coingecko.com/api/v3/simple/price?ids=${ids}&vs_currencies=usd&include_24hr_change=true&include_sparkline=true`);
      const prices = {};
      Object.entries(r.data).forEach(([id, data]) => {
        const REVERSE = { bitcoin:'BTC',ethereum:'ETH',binancecoin:'BNB',solana:'SOL',ripple:'XRP',cardano:'ADA',dogecoin:'DOGE',polkadot:'DOT','matic-network':'MATIC','avalanche-2':'AVAX',chainlink:'LINK' };
        const sym = REVERSE[id] || id.toUpperCase();
        prices[sym] = { price: data.usd, change24h: data.usd_24h_change };
      });
      setWatchlistPrices(prices);
    } catch (e) {}
  };

  const loadMovers = async () => {
    try {
      const r = await axios.get(`${API}/trading/market/movers`);
      setMovers(r.data);
    } catch (e) {}
  };

  const loadTopCoins = async () => {
    try {
      const r = await axios.get(`${API}/trading/market/top`, { params: { limit: 20 } });
      setTopCoins(r.data.coins || []);
    } catch (e) {}
  };

  const loadPaperTrades = async () => {
    try {
      const r = await axios.get(`${API}/trading/paper-trades/${user.id}`);
      setPaperTrades(r.data.trades || []);
    } catch (e) {}
  };

  const handleSearch = useCallback(async (q) => {
    setSearchQ(q);
    if (q.length < 1) { setSearchResults([]); return; }
    try {
      const r = await axios.get(`${API}/trading/market/search`, { params: { q } });
      setSearchResults(r.data.results || []);
    } catch (e) {}
  }, []);

  const selectSymbol = (sym) => {
    setSelectedSymbol(sym.toUpperCase());
    setSearchQ(''); setSearchResults([]); setShowSearch(false);
  };

  const addToWatchlist = async (symbol) => {
    if (!user?.id) return;
    try {
      await axios.post(`${API}/trading/watchlist/${user.id}`, { symbol });
      toast.success(`${symbol} agregado a watchlist`);
      loadWatchlist();
    } catch (e) { toast.error('Error al agregar a watchlist'); }
  };

  const removeFromWatchlist = async (symbol) => {
    if (!user?.id) return;
    try {
      await axios.delete(`${API}/trading/watchlist/${user.id}/${symbol}`);
      toast.success(`${symbol} eliminado`);
      loadWatchlist();
    } catch (e) {}
  };

  const submitPaperTrade = async () => {
    if (!user?.id || !paperForm.quantity || !paperForm.entry_price) {
      toast.error('Completa cantidad y precio de entrada'); return;
    }
    try {
      await axios.post(`${API}/trading/paper-trades/${user.id}`, {
        symbol: selectedSymbol,
        action: paperForm.action,
        quantity: parseFloat(paperForm.quantity),
        entry_price: parseFloat(paperForm.entry_price),
        stop_loss: paperForm.stop_loss ? parseFloat(paperForm.stop_loss) : null,
        take_profit: paperForm.take_profit ? parseFloat(paperForm.take_profit) : null,
        notes: paperForm.notes,
      });
      toast.success('Trade registrado en tu diario ✓');
      setShowPaperForm(false);
      loadPaperTrades();
    } catch (e) { toast.error('Error al registrar trade'); }
  };

  const closePaperTrade = async (tradeId) => {
    const price = analysis?.quote?.price;
    if (!price) { toast.error('Carga un análisis primero'); return; }
    try {
      const r = await axios.put(`${API}/trading/paper-trades/${user.id}/${tradeId}/close`, null, { params: { exit_price: price } });
      const pnl = r.data.pnl || 0;
      toast[pnl >= 0 ? 'success' : 'error'](`Cerrado con ${pnl >= 0 ? '+' : ''}${fmtPrice(pnl)} (${r.data.pnl_pct?.toFixed(2)}%)`);
      loadPaperTrades();
    } catch (e) { toast.error('Error al cerrar trade'); }
  };

  const quote = analysis?.quote;
  const signal = analysis?.signal;
  const ind = analysis?.indicators;
  const chart = analysis?.chart || [];
  const isPositive = (quote?.change_24h || 0) >= 0;

  // Prepare chart data
  const chartData = chart.map(c => ({
    ...c,
    date: new Date(c.ts).toLocaleDateString('es-MX', { month: 'short', day: 'numeric' }),
    close: c.close,
  }));

  const chartMin = chartData.length > 0 ? Math.min(...chartData.map(c => c.low || c.close)) * 0.998 : 0;
  const chartMax = chartData.length > 0 ? Math.max(...chartData.map(c => c.high || c.close)) * 1.002 : 0;

  return (
    <div className="h-full flex flex-col overflow-hidden" style={{ fontFamily: "'Inter', sans-serif" }}>

      {/* ── TOP BAR ── */}
      <div className="flex items-center gap-3 p-3 border-b border-white/5 bg-[#0a0a0a] flex-shrink-0">
        {/* Symbol Search */}
        <div className="relative flex-shrink-0">
          <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-3 py-2 cursor-pointer hover:border-white/20 transition-colors"
            onClick={() => { setShowSearch(!showSearch); setTimeout(() => searchRef.current?.focus(), 100); }}>
            {quote?.image && <img src={quote.image} alt="" className="w-5 h-5 rounded-full" onError={e => e.target.style.display='none'} />}
            <span className="text-white font-bold text-sm">{selectedSymbol}</span>
            <span className="text-gray-500 text-xs hidden sm:block">/ USD</span>
            <Search className="w-3.5 h-3.5 text-gray-500 ml-1" />
          </div>
          {showSearch && (
            <div className="absolute top-full left-0 mt-1 w-72 glass-panel border border-white/10 rounded-xl shadow-2xl z-50 overflow-hidden">
              <div className="p-2 border-b border-white/5">
                <input ref={searchRef} value={searchQ} onChange={e => handleSearch(e.target.value)}
                  placeholder="Buscar: BTC, ETH, SOL..."
                  className="w-full bg-transparent text-white text-sm outline-none px-2 py-1 placeholder-gray-600" autoFocus />
              </div>
              {searchResults.length > 0 ? (
                <div className="max-h-48 overflow-y-auto">
                  {searchResults.map(r => (
                    <button key={r.id} onClick={() => selectSymbol(r.symbol)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 transition-colors text-left">
                      {r.image && <img src={r.image} alt="" className="w-6 h-6 rounded-full" />}
                      <div>
                        <span className="text-white font-semibold text-sm">{r.symbol}</span>
                        <span className="text-gray-500 text-xs ml-2">{r.name}</span>
                      </div>
                      {r.market_cap_rank && <span className="ml-auto text-gray-600 text-xs">#{r.market_cap_rank}</span>}
                    </button>
                  ))}
                </div>
              ) : searchQ.length > 0 ? (
                <p className="text-gray-500 text-sm text-center py-4">Sin resultados</p>
              ) : (
                <div className="p-2 flex flex-wrap gap-1.5">
                  {['BTC','ETH','SOL','BNB','AVAX','LINK','DOT','ADA'].map(s => (
                    <button key={s} onClick={() => selectSymbol(s)}
                      className="px-2.5 py-1 bg-white/5 hover:bg-white/10 text-gray-300 text-xs rounded-lg transition-colors">
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Price ticker */}
        {quote ? (
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div>
              <span className="text-white font-mono font-bold text-lg leading-none">{fmtPrice(quote.price)}</span>
            </div>
            <div className={`flex items-center gap-1 text-sm font-semibold ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
              {isPositive ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              {fmtPct(quote.change_24h)}
            </div>
            <div className="hidden md:flex items-center gap-4 text-xs text-gray-500">
              <span>H: <span className="text-gray-300">{fmtPrice(quote.high_24h)}</span></span>
              <span>L: <span className="text-gray-300">{fmtPrice(quote.low_24h)}</span></span>
              <span>Vol: <span className="text-gray-300">{fmtNum(quote.volume_24h)}</span></span>
              <span>Mcap: <span className="text-gray-300">{fmtNum(quote.market_cap)}</span></span>
            </div>
          </div>
        ) : (
          <div className="flex-1 h-7 bg-white/5 rounded-lg animate-pulse" />
        )}

        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Add to watchlist */}
          <button onClick={() => addToWatchlist(selectedSymbol)}
            className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-yellow-400 transition-colors" title="Agregar a watchlist">
            <Star className="w-4 h-4" />
          </button>
          {/* Paper trade */}
          <button onClick={() => setShowPaperForm(true)}
            className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30 rounded-lg text-xs font-semibold transition-colors">
            <Plus className="w-3.5 h-3.5" /> Paper Trade
          </button>
          {/* Refresh */}
          <button onClick={loadAnalysis}
            className={`p-2 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 transition-colors ${loadingAnalysis ? 'animate-spin' : ''}`}>
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* ── SIGNAL BANNER (always visible) ── */}
      {signal && (
        <div className={`flex-shrink-0 px-4 py-2.5 border-b flex items-center gap-4 text-xs ${
          signal.signal === 'BUY' ? 'bg-green-500/5 border-green-500/20' :
          signal.signal === 'SELL' ? 'bg-red-500/5 border-red-500/20' :
          'bg-yellow-500/5 border-yellow-500/20'
        }`}>
          <SignalBadge signal={signal.signal} />
          <span className="text-gray-400 flex-1 truncate hidden sm:block">{signal.reasoning}</span>
          <div className="flex items-center gap-3 flex-shrink-0">
            <span className="text-gray-500">Confianza</span>
            <div className="w-20 h-1.5 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{
                width: `${signal.confidence}%`,
                background: signal.signal === 'BUY' ? '#22C55E' : signal.signal === 'SELL' ? '#EF4444' : '#F59E0B'
              }} />
            </div>
            <span className="text-white font-semibold">{signal.confidence}%</span>
          </div>
        </div>
      )}

      {/* ── MAIN CONTENT ── */}
      <div className="flex-1 overflow-y-auto">
        <div className="flex flex-col lg:flex-row h-full">

          {/* ── LEFT: WATCHLIST ── */}
          <div className="lg:w-52 xl:w-60 flex-shrink-0 border-b lg:border-b-0 lg:border-r border-white/5 bg-[#0a0a0a]">
            <div className="p-3 border-b border-white/5">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Watchlist</h3>
            </div>
            <div className="overflow-y-auto max-h-40 lg:max-h-full">
              {watchlist.map(item => {
                const p = watchlistPrices[item.symbol] || {};
                const pos = (p.change24h || 0) >= 0;
                return (
                  <button key={item.symbol} onClick={() => selectSymbol(item.symbol)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 hover:bg-white/5 transition-colors ${selectedSymbol === item.symbol ? 'bg-white/5 border-l-2 border-red-500' : ''}`}>
                    <div className="text-left">
                      <p className="text-white text-sm font-semibold">{item.symbol}</p>
                      <p className={`text-xs font-mono ${pos ? 'text-green-400' : 'text-red-400'}`}>
                        {p.price ? fmtPrice(p.price) : '—'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {p.change24h !== undefined && (
                        <span className={`text-xs font-semibold ${pos ? 'text-green-400' : 'text-red-400'}`}>
                          {fmtPct(p.change24h)}
                        </span>
                      )}
                      <button onClick={e => { e.stopPropagation(); removeFromWatchlist(item.symbol); }}
                        className="opacity-0 group-hover:opacity-100 p-0.5 hover:text-red-400 text-gray-600 transition-colors">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── CENTER ── */}
          <div className="flex-1 min-w-0 flex flex-col">

            {/* Section tabs */}
            <div className="flex items-center gap-1 px-4 py-2 border-b border-white/5 flex-shrink-0 overflow-x-auto">
              {[
                { id: 'chart', label: 'Gráfico', icon: BarChart2 },
                { id: 'indicators', label: 'Indicadores', icon: Activity },
                { id: 'signal', label: 'Señal IA', icon: Brain },
                { id: 'trades', label: 'Paper Trading', icon: BookOpen },
                { id: 'market', label: 'Mercado', icon: Layers },
              ].map(t => {
                const Icon = t.icon;
                return (
                  <button key={t.id} onClick={() => setActiveSection(t.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${
                      activeSection === t.id ? 'bg-red-500 text-white' : 'text-gray-500 hover:text-gray-300 hover:bg-white/5'
                    }`}>
                    <Icon className="w-3.5 h-3.5" />{t.label}
                  </button>
                );
              })}
              {/* Timeframe (only for chart) */}
              {activeSection === 'chart' && (
                <div className="ml-auto flex items-center gap-1">
                  {TIMEFRAMES.map(tf => (
                    <button key={tf.days} onClick={() => setTimeframe(tf.days)}
                      className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-colors ${
                        timeframe === tf.days ? 'bg-white/15 text-white' : 'text-gray-600 hover:text-gray-400'
                      }`}>{tf.label}</button>
                  ))}
                </div>
              )}
            </div>

            {/* ── CHART TAB ── */}
            {activeSection === 'chart' && (
              <div className="flex-1 p-4 min-h-0">
                {loadingAnalysis ? (
                  <div className="h-64 flex items-center justify-center">
                    <div className="text-center space-y-3">
                      <RefreshCw className="w-8 h-8 animate-spin text-red-500 mx-auto" />
                      <p className="text-gray-500 text-sm">Cargando análisis de {selectedSymbol}…</p>
                    </div>
                  </div>
                ) : chartData.length > 0 ? (
                  <div className="space-y-4">
                    <ResponsiveContainer width="100%" height={280}>
                      <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="priceGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={isPositive ? '#22C55E' : '#EF4444'} stopOpacity={0.25} />
                            <stop offset="95%" stopColor={isPositive ? '#22C55E' : '#EF4444'} stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                        <XAxis dataKey="date" tick={{ fill: '#4B5563', fontSize: 10 }} axisLine={false} tickLine={false}
                          interval={Math.floor(chartData.length / 6)} />
                        <YAxis domain={[chartMin, chartMax]} tick={{ fill: '#4B5563', fontSize: 10 }} axisLine={false}
                          tickLine={false} tickFormatter={v => fmtPrice(v)} width={72} />
                        <Tooltip content={<ChartTooltip />} />
                        {ind?.bollinger && (
                          <>
                            <ReferenceLine y={ind.bollinger.upper} stroke="rgba(59,130,246,0.3)" strokeDasharray="3 3" />
                            <ReferenceLine y={ind.bollinger.lower} stroke="rgba(59,130,246,0.3)" strokeDasharray="3 3" />
                          </>
                        )}
                        {ind?.ema?.ema20 && <ReferenceLine y={ind.ema.ema20} stroke="rgba(234,179,8,0.5)" strokeDasharray="2 3" label={{ value: 'EMA20', fill: '#EAB308', fontSize: 9, position: 'insideTopRight' }} />}
                        {ind?.ema?.ema50 && <ReferenceLine y={ind.ema.ema50} stroke="rgba(168,85,247,0.5)" strokeDasharray="2 3" label={{ value: 'EMA50', fill: '#A855F7', fontSize: 9, position: 'insideTopRight' }} />}
                        <Area type="monotone" dataKey="close" stroke={isPositive ? '#22C55E' : '#EF4444'} strokeWidth={1.5} fill="url(#priceGrad)" dot={false} activeDot={{ r: 4 }} />
                      </AreaChart>
                    </ResponsiveContainer>

                    {/* Key levels */}
                    {ind && (
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {[
                          { label: 'Soporte', val: ind.support_resistance?.support, color: '#22C55E' },
                          { label: 'Resistencia', val: ind.support_resistance?.resistance, color: '#EF4444' },
                          { label: 'EMA 20', val: ind.ema?.ema20, color: '#EAB308' },
                          { label: 'ATR', val: ind.atr, color: '#6366F1', pct: ind.volatility_pct },
                        ].map(({ label, val, color, pct }) => (
                          <div key={label} className="glass-panel p-3 rounded-xl">
                            <p className="text-gray-500 text-xs mb-1">{label}</p>
                            <p className="font-mono font-semibold text-sm" style={{ color }}>{fmtPrice(val)}</p>
                            {pct !== undefined && <p className="text-gray-600 text-xs">{pct}% vol</p>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="h-64 flex items-center justify-center text-gray-600">Sin datos de gráfico</div>
                )}
              </div>
            )}

            {/* ── INDICATORS TAB ── */}
            {activeSection === 'indicators' && (
              <div className="flex-1 p-4 space-y-4 overflow-y-auto">
                {!ind ? (
                  <div className="h-32 flex items-center justify-center">
                    <RefreshCw className="w-6 h-6 animate-spin text-gray-600" />
                  </div>
                ) : (
                  <>
                    {/* RSI */}
                    <div className="glass-panel p-4 rounded-xl">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-gray-200">RSI (14)</h3>
                        <span className={`text-xs px-2 py-0.5 rounded font-semibold ${
                          ind.rsi < 30 ? 'bg-green-500/20 text-green-400' :
                          ind.rsi > 70 ? 'bg-red-500/20 text-red-400' :
                          'bg-yellow-500/20 text-yellow-400'
                        }`}>{ind.rsi_signal?.toUpperCase()}</span>
                      </div>
                      <div className="flex items-center gap-6">
                        <RSIGauge value={ind.rsi} />
                        <div className="flex-1 space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-500">Valor actual</span>
                            <span className="text-white font-semibold">{ind.rsi}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Sobreventa (&lt;30)</span>
                            <span className="text-green-400">Compra potencial</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Sobrecompra (&gt;70)</span>
                            <span className="text-red-400">Venta potencial</span>
                          </div>
                          <div className="w-full h-2 bg-white/5 rounded-full overflow-hidden">
                            <div className="h-full rounded-full transition-all" style={{
                              width: `${ind.rsi}%`,
                              background: `linear-gradient(to right, #22C55E, #F59E0B, #EF4444)`
                            }} />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* MACD */}
                    <div className="glass-panel p-4 rounded-xl">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-gray-200">MACD</h3>
                        <span className={`text-xs px-2 py-0.5 rounded font-semibold ${
                          ind.macd?.trend === 'bullish' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                        }`}>{ind.macd?.trend?.toUpperCase()}</span>
                      </div>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div><p className="text-gray-500 text-xs mb-1">MACD Line</p><p className="font-mono text-white">{ind.macd?.macd?.toFixed(4)}</p></div>
                        <div><p className="text-gray-500 text-xs mb-1">Signal Line</p><p className="font-mono text-white">{ind.macd?.signal?.toFixed(4)}</p></div>
                        <div><p className="text-gray-500 text-xs mb-1">Histograma</p>
                          <p className={`font-mono font-semibold ${ind.macd?.histogram > 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {ind.macd?.histogram > 0 ? '+' : ''}{ind.macd?.histogram?.toFixed(4)}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* EMAs */}
                    <div className="glass-panel p-4 rounded-xl">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-gray-200">Medias Móviles (EMA)</h3>
                        <span className={`text-xs px-2 py-0.5 rounded font-semibold capitalize ${
                          ind.ema?.trend?.includes('bullish') ? 'bg-green-500/20 text-green-400' :
                          ind.ema?.trend?.includes('bearish') ? 'bg-red-500/20 text-red-400' :
                          'bg-gray-500/20 text-gray-400'
                        }`}>{ind.ema?.trend?.replace('_', ' ')}</span>
                      </div>
                      <div className="space-y-2">
                        {[
                          { label: 'EMA 20', val: ind.ema?.ema20, color: '#EAB308', period: '~4 semanas' },
                          { label: 'EMA 50', val: ind.ema?.ema50, color: '#A855F7', period: '~10 semanas' },
                          { label: 'EMA 200', val: ind.ema?.ema200, color: '#3B82F6', period: '~40 semanas' },
                        ].map(({ label, val, color, period }) => (
                          <div key={label} className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-0.5 rounded-full" style={{ background: color }} />
                              <span className="text-gray-400">{label}</span>
                              <span className="text-gray-600 text-xs">{period}</span>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="font-mono text-white">{val ? fmtPrice(val) : '—'}</span>
                              {val && quote?.price && (
                                <span className={`text-xs font-semibold ${quote.price > val ? 'text-green-400' : 'text-red-400'}`}>
                                  {quote.price > val ? '↑ precio arriba' : '↓ precio abajo'}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Bollinger Bands */}
                    <div className="glass-panel p-4 rounded-xl">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-gray-200">Bandas de Bollinger (20, 2σ)</h3>
                        {ind.bollinger?.squeeze && (
                          <span className="text-xs px-2 py-0.5 rounded font-semibold bg-purple-500/20 text-purple-400">⚡ SQUEEZE</span>
                        )}
                      </div>
                      <div className="space-y-3">
                        <div className="flex items-center gap-3 text-sm">
                          <div className="flex-1 space-y-1">
                            {[
                              { label: 'Banda Superior', val: ind.bollinger?.upper, note: 'resistencia dinámica' },
                              { label: 'Media (SMA20)', val: ind.bollinger?.middle, note: 'punto de equilibrio' },
                              { label: 'Banda Inferior', val: ind.bollinger?.lower, note: 'soporte dinámico' },
                            ].map(({ label, val, note }) => (
                              <div key={label} className="flex justify-between items-center">
                                <span className="text-gray-500">{label} <span className="text-gray-700 text-xs">({note})</span></span>
                                <span className="font-mono text-white text-xs">{fmtPrice(val)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between text-xs text-gray-500 mb-1">
                            <span>Posición del precio en las bandas</span>
                            <span className="text-white font-semibold">{ind.bollinger?.position?.toFixed(0)}%</span>
                          </div>
                          <div className="w-full h-3 bg-white/5 rounded-full overflow-hidden relative">
                            <div className="absolute inset-y-0 left-0 w-[15%] bg-green-500/20 rounded-l-full" />
                            <div className="absolute inset-y-0 right-0 w-[15%] bg-red-500/20 rounded-r-full" />
                            <div className="absolute top-0.5 bottom-0.5 w-2 bg-white rounded-full transition-all" style={{ left: `calc(${ind.bollinger?.position || 50}% - 4px)` }} />
                          </div>
                          <div className="flex justify-between text-xs text-gray-600 mt-1">
                            <span>← Sobrevendido</span><span>Sobrecomprado →</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* S&R */}
                    <div className="glass-panel p-4 rounded-xl">
                      <h3 className="text-sm font-semibold text-gray-200 mb-3">Soporte & Resistencia</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-green-500/5 border border-green-500/20 rounded-lg p-3">
                          <p className="text-green-400 text-xs font-semibold mb-1">SOPORTE</p>
                          <p className="text-white font-mono font-bold">{fmtPrice(ind.support_resistance?.support)}</p>
                          {quote?.price && <p className="text-gray-500 text-xs mt-1">{((quote.price - ind.support_resistance?.support) / quote.price * 100).toFixed(1)}% por debajo</p>}
                        </div>
                        <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-3">
                          <p className="text-red-400 text-xs font-semibold mb-1">RESISTENCIA</p>
                          <p className="text-white font-mono font-bold">{fmtPrice(ind.support_resistance?.resistance)}</p>
                          {quote?.price && <p className="text-gray-500 text-xs mt-1">{((ind.support_resistance?.resistance - quote.price) / quote.price * 100).toFixed(1)}% por arriba</p>}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ── AI SIGNAL TAB ── */}
            {activeSection === 'signal' && (
              <div className="flex-1 p-4 space-y-4 overflow-y-auto">
                {!signal ? (
                  <div className="h-32 flex items-center justify-center">
                    <RefreshCw className="w-6 h-6 animate-spin text-gray-600" />
                  </div>
                ) : (
                  <>
                    {/* Main signal card */}
                    <div className={`rounded-2xl p-6 border ${
                      signal.signal === 'BUY'  ? 'bg-green-500/5 border-green-500/25' :
                      signal.signal === 'SELL' ? 'bg-red-500/5 border-red-500/25' :
                      'bg-yellow-500/5 border-yellow-500/25'
                    }`}>
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <p className="text-gray-500 text-xs mb-2 uppercase tracking-wider">Señal OkTrade AI</p>
                          <SignalBadge signal={signal.signal} />
                        </div>
                        <div className="text-right">
                          <p className="text-gray-500 text-xs mb-1">Confianza</p>
                          <p className="text-white font-bold text-2xl">{signal.confidence}%</p>
                        </div>
                      </div>

                      {/* Confidence bar */}
                      <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden mb-4">
                        <div className="h-full rounded-full transition-all" style={{
                          width: `${signal.confidence}%`,
                          background: signal.signal === 'BUY' ? 'linear-gradient(to right, #16A34A, #22C55E)' :
                            signal.signal === 'SELL' ? 'linear-gradient(to right, #DC2626, #EF4444)' :
                            'linear-gradient(to right, #D97706, #F59E0B)'
                        }} />
                      </div>

                      {/* Reasoning */}
                      <p className="text-gray-300 text-sm leading-relaxed mb-4">{signal.reasoning}</p>

                      {/* Key factors */}
                      <div className="space-y-2 mb-4">
                        <p className="text-gray-500 text-xs uppercase tracking-wider">Factores clave</p>
                        {(signal.key_factors || []).map((f, i) => (
                          <div key={i} className="flex items-center gap-2 text-sm">
                            <div className={`w-1.5 h-1.5 rounded-full ${signal.signal === 'BUY' ? 'bg-green-400' : signal.signal === 'SELL' ? 'bg-red-400' : 'bg-yellow-400'}`} />
                            <span className="text-gray-300">{f}</span>
                          </div>
                        ))}
                      </div>

                      {/* Levels */}
                      <div className="grid grid-cols-3 gap-3">
                        {[
                          { label: 'Entrada', val: signal.entry_price, color: 'text-blue-400' },
                          { label: 'Stop Loss', val: signal.stop_loss, color: 'text-red-400' },
                          { label: 'Take Profit', val: signal.take_profit, color: 'text-green-400' },
                        ].map(({ label, val, color }) => (
                          <div key={label} className="glass-panel p-3 rounded-xl text-center">
                            <p className="text-gray-500 text-xs mb-1">{label}</p>
                            <p className={`font-mono font-bold text-sm ${color}`}>{fmtPrice(val)}</p>
                          </div>
                        ))}
                      </div>

                      {/* Risk/Reward */}
                      <div className="flex items-center gap-4 mt-4 pt-4 border-t border-white/5 text-sm">
                        <div className="flex items-center gap-2">
                          <Shield className="w-4 h-4 text-gray-500" />
                          <span className="text-gray-500">Riesgo/Beneficio:</span>
                          <span className="text-white font-semibold">1:{signal.risk_reward?.toFixed(1)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-gray-500" />
                          <span className="text-gray-500">Plazo:</span>
                          <span className="text-white font-semibold capitalize">{signal.timeframe}</span>
                        </div>
                        <div className="flex items-center gap-2 ml-auto">
                          <AlertTriangle className="w-4 h-4 text-gray-500" />
                          <span className="text-gray-500">Riesgo:</span>
                          <span className={`font-semibold capitalize ${signal.risk_level === 'bajo' ? 'text-green-400' : signal.risk_level === 'alto' ? 'text-red-400' : 'text-yellow-400'}`}>
                            {signal.risk_level}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Disclaimer */}
                    <div className="flex items-start gap-2 p-3 bg-white/5 rounded-xl text-xs text-gray-500">
                      <Info className="w-4 h-4 shrink-0 mt-0.5" />
                      <p>Esta señal es análisis técnico automatizado, no asesoría financiera. Opera siempre con gestión de riesgo y solo con capital que puedas permitirte perder.</p>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ── PAPER TRADING TAB ── */}
            {activeSection === 'trades' && (
              <div className="flex-1 p-4 space-y-4 overflow-y-auto">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-200">Diario de Paper Trading</h3>
                  <button onClick={() => setShowPaperForm(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30 rounded-lg text-xs font-semibold transition-colors">
                    <Plus className="w-3.5 h-3.5" /> Nuevo trade
                  </button>
                </div>

                {/* Stats summary */}
                {paperTrades.length > 0 && (() => {
                  const closed = paperTrades.filter(t => t.status === 'closed');
                  const totalPnl = closed.reduce((s, t) => s + (t.pnl || 0), 0);
                  const wins = closed.filter(t => (t.pnl || 0) > 0).length;
                  return (
                    <div className="grid grid-cols-3 gap-3">
                      <div className="glass-panel p-3 rounded-xl text-center">
                        <p className="text-gray-500 text-xs mb-1">P&L Total</p>
                        <p className={`font-mono font-bold ${totalPnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>{totalPnl >= 0 ? '+' : ''}{fmtPrice(totalPnl)}</p>
                      </div>
                      <div className="glass-panel p-3 rounded-xl text-center">
                        <p className="text-gray-500 text-xs mb-1">Win Rate</p>
                        <p className="font-bold text-white">{closed.length > 0 ? Math.round(wins / closed.length * 100) : 0}%</p>
                      </div>
                      <div className="glass-panel p-3 rounded-xl text-center">
                        <p className="text-gray-500 text-xs mb-1">Total trades</p>
                        <p className="font-bold text-white">{paperTrades.length}</p>
                      </div>
                    </div>
                  );
                })()}

                {paperTrades.length === 0 ? (
                  <div className="glass-panel p-8 text-center">
                    <BookOpen className="w-8 h-8 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-500 text-sm">No tienes trades registrados. El paper trading te permite practicar sin riesgo real.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {paperTrades.map(trade => (
                      <div key={trade.id} className="glass-panel p-4 rounded-xl">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-center gap-3">
                            <span className={`px-2 py-0.5 rounded text-xs font-bold ${trade.action === 'BUY' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                              {trade.action}
                            </span>
                            <div>
                              <span className="text-white font-semibold text-sm">{trade.symbol}</span>
                              <span className="text-gray-500 text-xs ml-2">{trade.quantity} unidades @ {fmtPrice(trade.entry_price)}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {trade.status === 'open' ? (
                              <>
                                <span className="flex items-center gap-1 text-xs text-yellow-400 bg-yellow-500/10 px-2 py-0.5 rounded-full">
                                  <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 animate-pulse" /> Abierto
                                </span>
                                <button onClick={() => closePaperTrade(trade.id)}
                                  className="text-xs text-gray-400 hover:text-white border border-white/10 hover:border-white/30 px-2 py-1 rounded transition-colors">
                                  Cerrar
                                </button>
                              </>
                            ) : (
                              <div className="flex items-center gap-2">
                                <span className={`font-mono font-bold text-sm ${(trade.pnl || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                  {(trade.pnl || 0) >= 0 ? '+' : ''}{fmtPrice(trade.pnl)} ({(trade.pnl_pct || 0).toFixed(1)}%)
                                </span>
                                {(trade.pnl || 0) >= 0
                                  ? <CheckCircle2 className="w-4 h-4 text-green-400" />
                                  : <XCircle className="w-4 h-4 text-red-400" />
                                }
                              </div>
                            )}
                          </div>
                        </div>
                        {trade.notes && <p className="text-gray-600 text-xs mt-2 border-t border-white/5 pt-2">{trade.notes}</p>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── MARKET TAB ── */}
            {activeSection === 'market' && (
              <div className="flex-1 p-4 space-y-6 overflow-y-auto">
                {/* Top movers */}
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-gray-200">Top Gainers 24h</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {movers.gainers.map(c => (
                      <button key={c.symbol} onClick={() => selectSymbol(c.symbol)}
                        className="glass-panel p-3 rounded-xl flex items-center gap-3 hover:bg-white/5 transition-colors text-left">
                        {c.image && <img src={c.image} alt="" className="w-8 h-8 rounded-full" />}
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-semibold text-sm">{c.symbol}</p>
                          <p className="text-gray-500 text-xs">{fmtPrice(c.price)}</p>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <span className="text-green-400 font-bold text-sm">{fmtPct(c.change_24h)}</span>
                          <Sparkline data={c.sparkline} positive={true} width={60} height={24} />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-gray-200">Top Losers 24h</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {movers.losers.map(c => (
                      <button key={c.symbol} onClick={() => selectSymbol(c.symbol)}
                        className="glass-panel p-3 rounded-xl flex items-center gap-3 hover:bg-white/5 transition-colors text-left">
                        {c.image && <img src={c.image} alt="" className="w-8 h-8 rounded-full" />}
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-semibold text-sm">{c.symbol}</p>
                          <p className="text-gray-500 text-xs">{fmtPrice(c.price)}</p>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <span className="text-red-400 font-bold text-sm">{fmtPct(c.change_24h)}</span>
                          <Sparkline data={c.sparkline} positive={false} width={60} height={24} />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Top 20 by market cap */}
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold text-gray-200">Top 20 por Market Cap</h3>
                  <div className="glass-panel rounded-xl overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-white/5 text-gray-500 uppercase tracking-wider">
                        <tr>
                          <th className="px-3 py-2 text-left">Activo</th>
                          <th className="px-3 py-2 text-right">Precio</th>
                          <th className="px-3 py-2 text-right">24h</th>
                          <th className="px-3 py-2 text-right hidden sm:table-cell">Market Cap</th>
                          <th className="px-3 py-2 text-right hidden sm:table-cell">7d</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/5">
                        {topCoins.map(c => (
                          <tr key={c.symbol} onClick={() => selectSymbol(c.symbol)}
                            className="hover:bg-white/5 cursor-pointer transition-colors">
                            <td className="px-3 py-2.5">
                              <div className="flex items-center gap-2">
                                {c.image && <img src={c.image} alt="" className="w-5 h-5 rounded-full" />}
                                <span className="text-white font-semibold">{c.symbol}</span>
                                <span className="text-gray-600 hidden sm:inline">{c.name}</span>
                              </div>
                            </td>
                            <td className="px-3 py-2.5 text-right font-mono text-white">{fmtPrice(c.price)}</td>
                            <td className={`px-3 py-2.5 text-right font-semibold ${c.change_24h >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {fmtPct(c.change_24h)}
                            </td>
                            <td className="px-3 py-2.5 text-right text-gray-400 hidden sm:table-cell">{fmtNum(c.market_cap)}</td>
                            <td className="px-3 py-2.5 hidden sm:table-cell">
                              <Sparkline data={c.sparkline} positive={c.change_24h >= 0} width={60} height={20} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>

      {/* ── PAPER TRADE FORM MODAL ── */}
      {showPaperForm && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="glass-panel border border-white/10 rounded-2xl p-6 w-full max-w-sm shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-white font-bold">Registrar Paper Trade</h2>
              <button onClick={() => setShowPaperForm(false)} className="text-gray-500 hover:text-white p-1"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <p className="text-gray-500 text-xs mb-2">Activo: <span className="text-white font-bold">{selectedSymbol}</span></p>
                <div className="flex gap-2">
                  {['BUY', 'SELL'].map(a => (
                    <button key={a} onClick={() => setPaperForm(f => ({ ...f, action: a }))}
                      className={`flex-1 py-2 rounded-lg text-sm font-bold transition-colors ${
                        paperForm.action === a
                          ? (a === 'BUY' ? 'bg-green-500 text-white' : 'bg-red-500 text-white')
                          : 'bg-white/5 text-gray-400 hover:bg-white/10'
                      }`}>{a === 'BUY' ? '↑ COMPRAR' : '↓ VENDER'}</button>
                  ))}
                </div>
              </div>
              {[
                { label: 'Cantidad', key: 'quantity', placeholder: '0.01', type: 'number' },
                { label: 'Precio de entrada', key: 'entry_price', placeholder: quote?.price?.toFixed(2) || '0', type: 'number' },
                { label: 'Stop Loss (opcional)', key: 'stop_loss', placeholder: signal?.stop_loss?.toFixed(4) || '0', type: 'number' },
                { label: 'Take Profit (opcional)', key: 'take_profit', placeholder: signal?.take_profit?.toFixed(4) || '0', type: 'number' },
              ].map(({ label, key, placeholder, type }) => (
                <div key={key}>
                  <label className="block text-xs text-gray-500 mb-1">{label}</label>
                  <input type={type} placeholder={placeholder} value={paperForm[key]}
                    onChange={e => setPaperForm(f => ({ ...f, [key]: e.target.value }))}
                    className="input-field w-full font-mono text-sm" />
                </div>
              ))}
              <div>
                <label className="block text-xs text-gray-500 mb-1">Notas</label>
                <textarea value={paperForm.notes} onChange={e => setPaperForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="¿Por qué entras en esta posición?" rows={2}
                  className="input-field w-full text-sm resize-none" />
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowPaperForm(false)}
                  className="flex-1 border border-white/10 text-gray-400 hover:bg-white/5 py-2.5 rounded-xl text-sm font-medium transition-colors">
                  Cancelar
                </button>
                <button onClick={submitPaperTrade}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-colors ${
                    paperForm.action === 'BUY' ? 'bg-green-500 hover:bg-green-600 text-white' : 'bg-red-500 hover:bg-red-600 text-white'
                  }`}>
                  Registrar {paperForm.action}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
